/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LevelOfDetail } from '../lod/LevelOfDetail';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { Swimlane } from 'src/app/app-data/models/Swimlane';
import { FancyAction } from 'src/app/common/fancy-firebase/base/FancyAction';
import { alternativeBuffer } from 'src/app/plain-javascript/services/rxjs-internal-extensions';
import { AbstractApplication } from '@appCanvasCommon/core/AbstractApplication';
import { Camera } from '@appCanvasCommon/core/Camera';
import { FancyContainer } from '@appCanvasCommon/graphics/FancyContainer';
import { FancyGraphics } from '@appCanvasCommon/graphics/FancyGraphics';
import { SwimlaneContainer } from './SwimlaneContainer';
import { SwimlaneRenderer } from './SwimlaneRenderer';

export interface ISwimlaneScreenPosition {
	screenTop: number;
	screenBottom: number;
}

// MOC-2897
// https://mocasystems.atlassian.net/wiki/spaces/FEAT/pages/480280577/Swim+Lane+Add+Ons
export class FancySwimlanes {
	/**
	 * Instance of FancyGraphics. Renders all of our swimlane rectangular areas
	 */
	private _renderer: FancyGraphics = new FancyGraphics();
	/**
	 * Our cache of SwimlaneLabels. Used to render text + text background
	 */
	private swimlanes: Map<string, SwimlaneContainer> = new Map();
	/**
	 * Reference to the camera
	 */
	private _camera: Camera;
	/**
	 * The container in the scene graph where all of our elements will be added too
	 */
	private _parent: FancyContainer;
	/**
	 * The container in the scene graph where all of our elements will be added too
	 */
	private _lineContainer: FancyContainer;

	/**
	 * Generates our swimlane graphics as caches as sprites.
	 */
	private _swimlaneRenderer: SwimlaneRenderer;

	private _app: AbstractApplication;
	private shutdownSubject$: Subject<boolean> = new Subject();
	constructor(
		public canvasAngularLand: CanvasAngularLand,
		parentLayer: FancyContainer,
		lineContainer: FancyContainer,
		camera: Camera,
		render: AbstractApplication
	) {
		parentLayer.addChild(this._renderer);

		// const logger = canvasAngularLand.loggerServiceFactory.create('FancySwimlanes');

		this._camera = camera;
		this._parent = parentLayer;
		this._lineContainer = lineContainer;
		this._app = render;
		// this._render = ()=>{
		// 	logger.log('swimlane render', renderQueue.isRunning);
		// 	render();
		// }

		// Generate swimlane textures
		this._swimlaneRenderer = new SwimlaneRenderer();

		// Watch for changes to the rawEvent$ stream.
		// Listening to rawEvents so we can filter down _what_ is happening to the swimlanes so we adjust our cache accordingly
		canvasAngularLand.swimlanes.data.rawEvent$
			.pipe(
				takeUntil(this.shutdownSubject$),
				alternativeBuffer(canvasAngularLand.renderQueueWithCleanup$)
				// tap(stuff => logger.log('fancy swimlanes tap', performance.now() - window.renderTimestamp) ),
				// filter(arr => !!arr.length),
				// map(thing => [thing])
			)
			.subscribe(this.update);
	}
	/**
	 * Updates our cache of swimlanes
	 * @param actions
	 */
	public update = (actions: FancyAction[]) => {
		actions.forEach((action: FancyAction) => {
			// Add action
			if (this.canvasAngularLand.swimlanes.data.isAddAction(action)) {
				// Swimlane added
				this.add(action.key, action.payload);
			} else if (this.canvasAngularLand.swimlanes.data.isRemoveAction(action)) {
				// Swimlane removed
				this.remove(action.key);
			} else {
				// Swimlane updated
				const swimlane: SwimlaneContainer = this.swimlanes.get(action.key);
				if (!swimlane) {
					this.add(action.key, action.payload);
				} else {
					swimlane.update(this._getRealWidth(), action.payload);
				}
			}
		});

		// Always re-draw after an update
		this._app.markAsDirty();
	};

	/**
	 * Forcefully updates the swimlanes by calculating a fake update action and then applying it to the view
	 * The swimlanes have a different update path compared to everything else in canvasland. A "cameraUpdate" event is
	 * dispatched whenever the camera is updated, which then emits a new action with a new model containing new screenTop
	 * and screenBottom values; which are then used to update the swimlane position on the screen.
	 *
	 * This update flow is delayed compared to other services which watch the camera directly, and results in swimlanes
	 * not updating in time when the camera is programmatically updated, like for plan print.
	 * So instead of fiddling with very old code, and getting this working asynchronously, this hack is to simulate a
	 * camera changed action from FancyFirebaseList and then force update each swimlane.
	 */
	public forceUpdate(): void {
		this.swimlanes.forEach((swimlaneContainer: SwimlaneContainer) => {
			const data: Swimlane = swimlaneContainer.getData();
			const newScreen: ISwimlaneScreenPosition = {
				screenTop: this._camera.realToScreenY(data.view.top),
				screenBottom: this._camera.realToScreenY(data.view.bottom),
			};
			swimlaneContainer.updatePosition(this._getRealWidth(), newScreen);
		});
	}

	/**
	 * Adds a new swimlane to our cache for rendering.
	 * @param key The unique ID
	 * @param swimlaneModel
	 */
	public add(key: string, swimlaneModel: Swimlane): FancySwimlanes {
		const swimlane: SwimlaneContainer = new SwimlaneContainer(swimlaneModel, this.canvasAngularLand);
		swimlane.update(this._getRealWidth(), swimlaneModel);

		this.swimlanes.set(key, swimlane);

		this._parent.addChild(swimlane, swimlane.label.background as any, swimlane.label);
		this._lineContainer.addChild(swimlane.lineContainer);

		return this;
	}
	/**
	 * Removes a swimlane from our cache so it is no longer drawn.
	 * @param {string} id
	 */
	public remove(id: string) {
		const thing = this.swimlanes.get(id);
		this.swimlanes.delete(id);
		if (thing) {
			thing.destroy();
		}
	}
	/**
	 * DESTROY ALL THE THINGS
	 * @param options
	 */
	public destroy(options?) {
		this.shutdownSubject$.next(true);

		this.swimlanes.forEach((swimlane: SwimlaneContainer) => {
			swimlane.destroy();
		});

		this._swimlaneRenderer.destroy(options);

		this.swimlanes.clear();
		this.swimlanes = null;

		this._app = null;
		this.canvasAngularLand = null;
	}

	/**
	 * Helper method to disable every label (and label container).
	 */
	public hideAllLabels(): void {
		this.swimlanes.forEach((swimlane: SwimlaneContainer) => {
			swimlane.hideLabel();
		});
	}
	/**
	 * Helper method to enable every label (and label container).
	 */
	public showAllLabels(): void {
		this.swimlanes.forEach((swimlane: SwimlaneContainer) => {
			swimlane.showLabel();
		});
	}

	/**
	 * Helper method which returns the real width of the swimlane. Handles the printing case where the renderer is larger
	 * than the screen. (Swimlanes are rendered to screen dimensions)
	 */
	private _getRealWidth(): number {
		return LevelOfDetail.isForced ? this._app.renderer.width : this._camera.view.width;
	}
}
